import { useMemo, useState } from "react";
import { pickEntityImage, fallbackForType, GLOBAL_FALLBACK, resolveImg } from "@/lib/imagePicker";

/**
 * SmartImage — robust <img> with the Dataleo image fallback chain
 * AND automatic logo-vs-photo fit detection.
 *
 * Priority for resolved src:
 *   1. explicit `src` prop (already-resolved URL)
 *   2. `entity` + `type` via pickEntityImage(...) — fallback seeded by entity id
 *   3. type fallback pool (seeded by entity id when available)
 *   4. global fallback
 *
 * Logo-aware fit:
 *   When `fit` is the default "cover", the component compares the image's
 *   natural aspect ratio to the rendered frame's aspect ratio on load. If
 *   they differ by more than ~60% (typical for company logos: very wide
 *   wordmarks or square stamps inside 16:9 cards), it switches to
 *   `object-contain` with a soft background so the logo is shown in full
 *   instead of being cropped. Real photos remain `object-cover` as before.
 *   Callers that *want* contain (e.g. job logos in small squares) can still
 *   pass `fit="contain"` explicitly to skip the heuristic.
 */
const FRAME_DEVIATION_THRESHOLD = 1.4; // ≥1.4x or ≤1/1.4 → use contain

// Intrinsic aspect-ratio hints per content type. Setting `width`/`height`
// attrs on every <img> lets the browser reserve layout space before the
// image loads — the single biggest CLS lever for our card-heavy layout.
// CSS still controls the rendered size; these are dimensionless hints.
const TYPE_DIMENSIONS = {
  news: { width: 1600, height: 900 },
  insight: { width: 1600, height: 900 },
  alert: { width: 1600, height: 900 },
  tutorial: { width: 1600, height: 900 },
  job: { width: 200, height: 200 },        // company logos
  ecosystem: { width: 200, height: 200 },
};

const SmartImage = ({
  src,
  entity,
  type = "news",
  alt = "",
  className = "",
  loading = "lazy",
  ecosystemLogo,
  fit = "cover", // "cover" | "contain" — explicit value takes precedence
  width,
  height,
  ...rest
}) => {
  const seed = entity?.id || entity?.slug || entity?.title || "";
  const dims = TYPE_DIMENSIONS[type] || TYPE_DIMENSIONS.news;
  const w = width ?? dims.width;
  const h = height ?? dims.height;

  const initial = useMemo(() => {
    if (src) {
      const resolved = resolveImg(src);
      if (resolved) return resolved;
    }
    if (entity) return pickEntityImage(entity, type, { ecosystemLogo });
    return fallbackForType(type, seed);
  }, [src, entity, type, ecosystemLogo, seed]);

  const [current, setCurrent] = useState(initial);
  const [stage, setStage] = useState(0);
  // Auto-detected fit. Null until first load; once detected, overrides cover.
  const [autoFit, setAutoFit] = useState(null);

  const onError = () => {
    if (stage === 0) {
      setStage(1);
      setCurrent(fallbackForType(type, seed));
      setAutoFit(null);
    } else if (stage === 1) {
      setStage(2);
      setCurrent(GLOBAL_FALLBACK);
      setAutoFit(null);
    } else {
      setStage(3);
    }
  };

  const onLoad = (e) => {
    // Only auto-fit when the caller did not explicitly request "contain".
    if (fit !== "cover") return;
    const img = e.currentTarget;
    const nw = img.naturalWidth || 0;
    const nh = img.naturalHeight || 0;
    const fw = img.offsetWidth || 0;
    const fh = img.offsetHeight || 0;
    if (!nw || !nh || !fw || !fh) return;
    const natural = nw / nh;
    const frame = fw / fh;
    const ratio = natural / frame;
    const next = (ratio >= FRAME_DEVIATION_THRESHOLD || ratio <= 1 / FRAME_DEVIATION_THRESHOLD)
      ? "contain"
      : "cover";
    if (next !== autoFit) setAutoFit(next);
  };

  if (stage >= 3) {
    return <div className={`bg-[#F3F4F6] ${className}`} aria-hidden="true" />;
  }

  const effectiveFit = autoFit || fit;
  const fitCls = effectiveFit === "contain" ? "object-contain bg-[#F7F7F2]" : "object-cover";
  return (
    <img
      src={current}
      alt={alt}
      loading={loading}
      decoding="async"
      width={w}
      height={h}
      onError={onError}
      onLoad={onLoad}
      className={`${fitCls} ${className}`}
      {...rest}
    />
  );
};

export default SmartImage;
